import React, { FormEvent, PropsWithChildren } from 'react';
import { FieldControl, FieldGroup, FormBuilder } from 'react-reactive-form';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import RadioButton from '../forms/RadioButton';
import { CarClassModel } from '../../api/core/core.types';
import { ItemPriceCarClassModel, ItemPriceExceptionModel, UnitType } from '../../api/pricing/pricing.types';
import Checkbox from '../forms/Checkbox';
import { NumberInput, TextInput } from '../forms/Input';
import { TwoColumnsSelectValueModel } from '../forms/TwoColumnsSelect';
import { MultiSelect } from '../forms/MultiSelect';
import { WithTranslation, withTranslation } from 'react-i18next';

interface ItemPriceCarClassModalProps extends WithTranslation {
  toggle: () => void;
  isOpen: boolean;
  priceCurrency?: string;
  priceType: UnitType;
  itemPriceCarClass?: ItemPriceCarClassModel | null;
  carClasses: CarClassModel[];
  onConfirm: (itemPriceCarClass: ItemPriceCarClassModel) => void;
  onCancel: () => void;
  onItemDelete: (itemPriceCarClassId: number) => void;
  acrissCodesInUse: Map<number, string[]>;
}

interface ItemPriceCarClassState {
  selectedCarClasses: string[];
  acrissCodesInUse: string[];
}

class ItemPriceCarClassModal extends React.PureComponent<PropsWithChildren<ItemPriceCarClassModalProps>, ItemPriceCarClassState> {

  readonly state = {
    selectedCarClasses: [],
    acrissCodesInUse: []
  };

  form = FormBuilder.group({
    carClassSelection: [ 'all' ],
    carClasses: [ { items: [], selected: [] } ],
    price: [ undefined ],
    maxPrice: [ undefined ],
    supplierReferenceCode: [ undefined ],
    hasException: [ false ]
  });

  componentDidMount() {
    this.assignCarModelsToForm(this.props.carClasses);
  }

  componentDidUpdate(prevProps: ItemPriceCarClassModalProps) {
    if (this.props.isOpen !== prevProps.isOpen) {
      this.getUsedAcrissCodes();
    }

    if (this.props.itemPriceCarClass !== prevProps.itemPriceCarClass) {
      this.resetFormValues().then((r) => {
        this.setState({ selectedCarClasses: [] });
        this.assignCarModelsToForm(this.props.carClasses);
        this.updateFormValue();
        this.getUsedAcrissCodes();
      });
    }

    if ((!this.props.itemPriceCarClass && !prevProps.itemPriceCarClass) && (this.props.isOpen && !prevProps.isOpen)) {
      this.handleDeselectAllOptions();
      this.setState({ selectedCarClasses: [] });
      this.getUsedAcrissCodes();
    }

    if (this.props.itemPriceCarClass && !prevProps.itemPriceCarClass) {
      this.updateFormValue();
      this.getUsedAcrissCodes();
    }

    if (this.props.carClasses.length !== prevProps.carClasses.length) {
      this.assignCarModelsToForm(this.props.carClasses);
    }
  }

  onDeleteClick() {
    const carClass = this.props.itemPriceCarClass;
    if (carClass && carClass.id) {
      this.props.onItemDelete(carClass.id);
    }
  }

  getPriceUnit = () => {
    const { t } = this.props;

    if (this.props.priceType === UnitType.PER_CENT) return '%';

    if (this.props.priceType === UnitType.FIXED) {
      return t('v.item_price.price.fixed',
        { currency: this.props.priceCurrency ? this.props.priceCurrency : '' });
    }

    return t('v.item_price.price.per_day',
      { currency: this.props.priceCurrency ? this.props.priceCurrency : '' });
  };

  getUsedAcrissCodes = (): string[] => {
    const keys: number[] = Array.from(this.props.acrissCodesInUse.keys());
    let usedAcrissCodes: string[] = [];

    keys.forEach((key) => {
      if (!this.props.itemPriceCarClass || (this.props.itemPriceCarClass && key !== this.props.itemPriceCarClass.id)) {
        const acrissCodes = this.props.acrissCodesInUse.get(key);

        if (acrissCodes) {
          usedAcrissCodes = [ ...usedAcrissCodes, ...acrissCodes ];
        }
      }
    });

    this.setState({
      acrissCodesInUse: usedAcrissCodes
    }, () => {
      this.forceUpdate();
    });
    return usedAcrissCodes;
  };

  // eslint-disable-next-line react/sort-comp
  updateFormValue = () => {
    if (!this.props.itemPriceCarClass) {
      return;

    }
    this.form.patchValue({
      carClassSelection: this.props.itemPriceCarClass.acrissCodes.length ? 'selection' : 'all',
      carClasses: {
        ...this.form.controls.carClasses.value,
        selected: this.props.itemPriceCarClass ? this.form.controls.carClasses.value.selected.filter((v: any) => !!this.props.itemPriceCarClass!.acrissCodes.find((val) => val === v)) : []
      }
    });
    if (this.props.itemPriceCarClass) this.setState({ selectedCarClasses: this.props.itemPriceCarClass.acrissCodes });

    if (this.props.itemPriceCarClass.itemPriceException && this.props.itemPriceCarClass.itemPriceException.price) {
      const itemPriceException = this.props.itemPriceCarClass.itemPriceException;
      this.form.patchValue({
        hasException: true,
        price: itemPriceException.price,
        maxPrice: itemPriceException.maxPrice,
        supplierReferenceCode: itemPriceException.supplierReferenceCode
      });
    }
  };

  getSelectedAcrissCodes = () => {
    const acrissCodes: string[] = [];

    if (this.form.controls.carClassSelection.value !== 'all') {
      return this.state.selectedCarClasses;
    }
    let noClassesPreviouslySelected = true;
    const keys: number[] = Array.from(this.props.acrissCodesInUse.keys());

    if (!this.props.itemPriceCarClass) {
      keys.forEach((key) => {
        const carClassAcrissCodes = this.props.acrissCodesInUse.get(key);
        if (carClassAcrissCodes && carClassAcrissCodes.length) noClassesPreviouslySelected = false;
      });
    }

    if (noClassesPreviouslySelected) {
      return [];
    }

    this.props.carClasses.forEach((c) => {
      if (!this.isAcrissCodeAssigned(c.acrissCode) && !acrissCodes.find((a) => a === c.acrissCode)) {
        acrissCodes.push(c.acrissCode);
      }
    });

    return acrissCodes;
  };

  isAcrissCodeAssigned = (acrissCode: string) => {
    let isCodeAssigned = false;
    const keys: number[] = Array.from(this.props.acrissCodesInUse.keys());

    keys.forEach((key) => {
      if (!this.props.itemPriceCarClass || (this.props.itemPriceCarClass && key !== this.props.itemPriceCarClass.id)) {
        const acrissCodes = this.props.acrissCodesInUse.get(key);

        if (acrissCodes) {
          if (acrissCodes.find((code) => code === acrissCode)) isCodeAssigned = true;
        }

      }
    });

    return isCodeAssigned;
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();

    if (this.form.invalid) {
      return;
    }

    const acrissCodes = this.getSelectedAcrissCodes();

    let id: number;
    if (this.props.itemPriceCarClass && this.props.itemPriceCarClass.id) id = this.props.itemPriceCarClass.id;
    else id = Array.from(this.props.acrissCodesInUse.keys()).length + 1;

    const exception: ItemPriceExceptionModel = {
      price: form.price.value,
      maxPrice: form.maxPrice.value,
      supplierReferenceCode: form.supplierReferenceCode.value
    };

    const itemPriceCarClass: ItemPriceCarClassModel = {
      id,
      acrissCodes,
      itemPriceException: form.hasException.value ? exception : undefined
    };

    this.props.onConfirm(itemPriceCarClass);
  };

  handleCarClassSelectValueChange = (v: any) => {
    this.setState({ selectedCarClasses: v.map((itm: any) => itm.acrissCode) });
    this.form.patchValue({
      carClasses: {
        ...this.form.controls.carClasses.value,
        selected: v.map((itm: any) => itm.acrissCode)
      }
    });
  };

  resetFormValues = async () => {
    this.form.reset();
    this.form.controls.carClasses.patchValue({ items: [], selected: [] });
  };

  assignCarModelsToForm = (carModels: CarClassModel[]) => {
    const prev = this.form.controls.carClasses.value as TwoColumnsSelectValueModel;
    const renderedAcrissCodes: string[] = [];

    carModels.forEach((carModel) => {
      if (!renderedAcrissCodes.find((a) => a === carModel.acrissCode)) {
        prev.items.push(carModel);

        renderedAcrissCodes.push(carModel.acrissCode);
      }
    });
    if (this.props.itemPriceCarClass) {
      this.props.itemPriceCarClass.acrissCodes.forEach((c) => {
        prev.selected.push(c);
      });
      this.setState({
        selectedCarClasses: this.props.itemPriceCarClass.acrissCodes
          .map((c) => c)
      });
    }
  };

  handleDeselectAllOptions() {
    this.form.patchValue({
      price: undefined,
      carClassSelection: 'all',
      hasException: false,
      maxPrice: undefined,
      supplierReferenceCode: undefined,
      carClasses: { ...this.form.controls.carClasses.value, selected: [] }
    });
  }

  render() {
    const { t } = this.props;

    return (
      <Modal isOpen={this.props.isOpen} toggle={this.props.toggle} autoFocus={false} size={'xl'}>
        <ModalHeader toggle={this.props.toggle}>{!this.props.itemPriceCarClass ?
          t('modal_title.item_price_car_class.add_car_classes') :
          t('modal_title.item_price_car_class.edit_car_classes')}
        </ModalHeader>
        <ModalBody>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal pl-1 pr-1" onSubmit={(e) => this.onSubmit(e)}>
                <div className="form-group row scrolling-div ">
                  <div className="col-2 d-flex align-items-start justify-content-end">
                    <label>{t('l.item_price_car_class_modal.car_classes')}:</label>
                  </div>
                  <div className="col-6 d-flex align-items-start justify-content-start">
                    <div className="d-flex flex-column flex-wrap">
                      <div className="pb-1">
                        <FieldControl
                          strict={false}
                          name="carClassSelection"
                          render={(props) => <RadioButton {...props} fieldValue="all"
                                                          id="car_all">{t('l.selection.all')}</RadioButton>}
                        />
                        <FieldControl
                          strict={false}
                          name="carClassSelection"
                          render={(props) => <RadioButton {...props}
                                                          fieldValue="selection" id="car_sel">
                            {t('l.selection.selected')}
                          </RadioButton>}
                        />
                      </div>
                      {
                        this.form.controls.carClassSelection.value === 'selection'
                        && (
                          <FieldControl
                            name="carClasses"
                            render={(v) => (
                              <MultiSelect
                                {...v}
                                selectedAttributeName="acrissCode"
                                sortAttributeName="acrissCode"
                                handleValueChange={(val: any) => this.handleCarClassSelectValueChange(val)}
                                filterAttributes={[ 'name', 'acrissCode' ]}
                                primaryAtr="acrissCode"
                                disabledValues={this.state.acrissCodesInUse}
                                secondaryAtr="name"
                              />
                            )}
                          />
                        )
                      }
                    </div>

                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-2 d-flex align-items-center justify-content-end">
                    <label>{t('l.item_price_car_class_modal.has_price_exception')}:</label>
                  </div>
                  <div className="col-6 d-flex align-items-center justify-content-start">
                    <FieldControl
                      strict={false}
                      name="hasException"
                      render={(props) => <Checkbox {...props} id="hasException" className="mt-1"
                                                   disabled={false}></Checkbox>}
                    />
                  </div>
                </div>
                {this.form.controls.hasException.value && (
                  <div>
                    <div className="form-group row">
                      <div className="col-6 d-flex align-items-center justify-content-end">
                        <label>{t('l.item_price_car_class_modal.price')} ({this.getPriceUnit()}):</label>
                      </div>
                      <div className="col-3 d-flex align-items-center justify-content-start">
                        <FieldControl
                          strict={false}
                          name="price"
                          render={NumberInput}
                        />
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-6 d-flex align-items-center justify-content-end">
                        <label>{t('l.item_price_car_class_modal.max_price')} ({this.getPriceUnit()}):</label>
                      </div>
                      <div className="col-3 d-flex align-items-center justify-content-start">
                        <FieldControl
                          strict={false}
                          name="maxPrice"
                          render={NumberInput}
                        />
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-6 d-flex align-items-center justify-content-end">
                        <label>{t('l.item_price_car_class_modal.supplier_reference_code')}:</label>
                      </div>
                      <div className="col-3 d-flex align-items-center justify-content-start">
                        <FieldControl
                          strict={false}
                          name="supplierReferenceCode"
                          render={TextInput}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className={`row ${this.props.itemPriceCarClass ? 'justify-content-between' : 'justify-content-end'}`}>
                  {this.props.itemPriceCarClass && (
                    <div>
                      <button type="button" className="btn btn-danger mr-2"
                              onClick={(e) => this.onDeleteClick()}>
                        {t('b.delete_Generic delete button').toUpperCase()}
                      </button>
                    </div>
                  )}
                  <div className="mt-1">
                    <button type="button" className="btn btn-outline-secondary btn mr-2" onClick={this.props.onCancel}>
                      {t('b.cancel_Generic cancel button translation').toUpperCase()}
                    </button>
                    <button type="submit" className="btn btn-primary btn" onClick={this.onSubmit}>
                      {t('b.save_Generic save button translation').toUpperCase()}
                    </button>
                  </div>
                </div>
              </form>
            )}
          />
        </ModalBody>
      </Modal>
    );
  }
}

export default withTranslation()(ItemPriceCarClassModal);
